/**
 * AgentReviewsList — the agent evaluation FLAG/FAIL review queue
 * (`agent_run_reviews`) on the admin issue-triage surface.
 *
 * #4966: until this change the list was display-only and nothing could move a
 * review out of `new` (255 of 255 open on 2026-09-29). Reviews repeat by
 * pattern, so the unit of work is a FILTERED SET, not a row: filter by agent +
 * verdict, "Select all shown", give one reason, then "Confirm defect" or
 * "Dismiss as noise". Every change is stamped server-side with who, when, why.
 *
 *  - Data comes from the `agentInfra_listRunReviews` callable (the collection
 *    is deny-all to clients by design), paged with "Load more".
 *  - Status changes go through `agentInfra_resolveRunReviews`.
 *  - The rationale is rendered exactly as stored — it is capped upstream
 *    (200 chars historically, 4,000 for newer docs); do not "fix" it here.
 *  - Filters are client-side over the loaded pages.
 *  - Three exit states, never two: loading / error / empty are each
 *    distinct — an empty queue must never look like a failed query.
 */
import React, { useMemo, useState } from "react";
import { AlertTriangle, Inbox, RefreshCw } from "lucide-react";
import { Button } from "../forms/Button";
import { OPEN_REVIEW_STATUSES, useAgentRunReviews } from "../../hooks/issueTriage/useAgentRunReviews";
import { isUnverifiableOnly } from "../../lib/agentReviews/flagReasons";
import {
  MAX_RESOLVE_BATCH,
  useResolveRunReviews,
  type ReviewResolution,
} from "../../hooks/issueTriage/useResolveRunReviews";

const VERDICT_BADGE: Record<string, string> = {
  FAIL: "bg-red-100 text-red-700",
  FLAG: "bg-amber-100 text-amber-700",
};

type StatusFilter = "open" | "resolved" | "all";
/** "UNVERIFIABLE" = flagged only for evidence our size caps withheld (#4966). */
type VerdictFilter = "all" | "FLAG" | "FAIL" | "UNVERIFIABLE";

function matchesVerdictFilter(
  review: { verdict: string; flagReasons?: readonly string[] },
  filter: VerdictFilter
): boolean {
  if (filter === "all") return true;
  if (filter === "UNVERIFIABLE") return isUnverifiableOnly(review);
  return review.verdict === filter;
}
const MAX_REASON_CHARS = 500;

function formatCreatedAt(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export function AgentReviewsList() {
  const {
    data, isLoading, isError, isFetching, refetch,
    hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useAgentRunReviews();
  const resolve = useResolveRunReviews();
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>("all");
  const [agentFilter, setAgentFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [reason, setReason] = useState("");

  const reviews = useMemo(() => data?.reviews ?? [], [data]);

  const agentOptions = useMemo(
    () => Array.from(new Set(reviews.map((r) => r.agentConfigId))).sort(),
    [reviews]
  );

  const filtered = useMemo(
    () =>
      reviews.filter((r) => {
        const isOpen = OPEN_REVIEW_STATUSES.has(r.status);
        return (
          matchesVerdictFilter(r, verdictFilter) &&
          (agentFilter === "all" || r.agentConfigId === agentFilter) &&
          (statusFilter === "all" || (statusFilter === "open") === isOpen)
        );
      }),
    [reviews, verdictFilter, agentFilter, statusFilter]
  );

  const selectableShown = useMemo(
    () => filtered.filter((r) => OPEN_REVIEW_STATUSES.has(r.status)).map((r) => r.runId),
    [filtered]
  );
  // Only ids still shown and still open count — a filter change never acts on hidden rows.
  const selectedShown = selectableShown.filter((id) => selected.has(id));
  const allShownSelected = selectableShown.length > 0 && selectedShown.length === selectableShown.length;
  const trimmedReason = reason.trim();
  const canResolve =
    selectedShown.length > 0 &&
    selectedShown.length <= MAX_RESOLVE_BATCH &&
    trimmedReason.length > 0 &&
    trimmedReason.length <= MAX_REASON_CHARS &&
    !resolve.isPending;

  const toggle = (runId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });

  const toggleAllShown = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allShownSelected) selectableShown.forEach((id) => next.delete(id));
      else selectableShown.forEach((id) => next.add(id));
      return next;
    });

  const submit = (status: ReviewResolution) => {
    if (!canResolve) return;
    resolve.mutate(
      { runIds: selectedShown, status, reason: trimmedReason },
      {
        onSuccess: () => {
          setSelected(new Set());
          setReason("");
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div data-testid="agent-reviews-loading" className="space-y-3">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-20 rounded-xl border border-slate-200 bg-slate-50 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div
        data-testid="agent-reviews-error"
        role="alert"
        className="rounded-xl border border-red-200 bg-red-50 p-6 text-center"
      >
        <AlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-2" />
        <p className="text-sm font-medium text-red-800">
          Couldn't load agent reviews — this is a query failure, not an empty queue.
        </p>
        <Button variant="danger" size="sm" className="mt-3" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4" />
          Retry
        </Button>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div
        data-testid="agent-reviews-empty"
        className="rounded-xl border border-slate-200 bg-white p-8 text-center"
      >
        <Inbox className="w-6 h-6 text-slate-400 mx-auto mb-2" />
        <p className="text-sm text-slate-600">
          No agent reviews in the queue — every graded run passed, or grading has
          not produced FLAG/FAIL verdicts yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Filters + truncation banner */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor="agent-reviews-status-filter" className="text-sm text-slate-600">
            Status
          </label>
          <select
            id="agent-reviews-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white"
          >
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="agent-reviews-agent-filter" className="text-sm text-slate-600">
            Agent
          </label>
          <select
            id="agent-reviews-agent-filter"
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white"
          >
            <option value="all">All agents</option>
            {agentOptions.map((agent) => (
              <option key={agent} value={agent}>
                {agent}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="agent-reviews-verdict-filter" className="text-sm text-slate-600">
            Verdict
          </label>
          <select
            id="agent-reviews-verdict-filter"
            value={verdictFilter}
            onChange={(e) => setVerdictFilter(e.target.value as VerdictFilter)}
            className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white"
          >
            <option value="all">All verdicts</option>
            <option value="FAIL">FAIL</option>
            <option value="FLAG">FLAG</option>
            <option value="UNVERIFIABLE">Unverifiable (evidence capped)</option>
          </select>
        </div>
        {isFetching && !isFetchingNextPage && <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />}
        {data && data.total > reviews.length && (
          <span
            data-testid="agent-reviews-truncation"
            className="ml-auto text-xs text-slate-500"
          >
            Showing newest {reviews.length} of {data.total} reviews
          </span>
        )}
      </div>

      {/* Selection + bulk actions */}
      {selectableShown.length > 0 && (
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={allShownSelected} onChange={toggleAllShown} />
          Select all shown
        </label>
      )}
      {selectedShown.length > 0 && (
        <div
          data-testid="agent-reviews-bulk-bar"
          className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2"
        >
          <p className="text-sm font-medium text-slate-800">
            {selectedShown.length} selected
            {selectedShown.length > MAX_RESOLVE_BATCH && ` — at most ${MAX_RESOLVE_BATCH} per action`}
          </p>
          <label htmlFor="agent-reviews-reason" className="block text-sm text-slate-600">
            Reason (required, shown in the audit trail)
          </label>
          <textarea
            id="agent-reviews-reason"
            value={reason}
            maxLength={MAX_REASON_CHARS}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="warning"
              size="sm"
              disabled={!canResolve}
              loading={resolve.isPending}
              loadingText="Saving..."
              onClick={() => submit("confirmed")}
            >
              Confirm defect
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!canResolve}
              loading={resolve.isPending}
              loadingText="Saving..."
              onClick={() => submit("dismissed")}
            >
              Dismiss as noise
            </Button>
          </div>
        </div>
      )}
      {resolve.isError && (
        <p data-testid="agent-reviews-resolve-error" role="alert" className="text-sm text-red-700">
          Couldn't resolve those reviews — nothing was changed. Try again.
        </p>
      )}
      {resolve.data && (
        <p data-testid="agent-reviews-resolve-result" className="text-sm text-slate-600">
          Resolved {resolve.data.updated.length}, skipped {resolve.data.skipped.length}
          {resolve.data.skipped.length > 0 && " (already resolved, or no longer in the queue)"}
        </p>
      )}

      {/* Rows */}
      {filtered.length === 0 ? (
        <div
          data-testid="agent-reviews-filtered-empty"
          className="rounded-xl border border-slate-200 bg-white p-6 text-center"
        >
          <p className="text-sm text-slate-600">
            No reviews match the current filters.
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => {
              setVerdictFilter("all");
              setAgentFilter("all");
              setStatusFilter("all");
            }}
          >
            Clear filters
          </Button>
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((review) => {
            const isOpen = OPEN_REVIEW_STATUSES.has(review.status);
            return (
              <li
                key={review.runId}
                data-testid={`agent-review-row-${review.runId}`}
                className="rounded-xl border border-slate-200 bg-white p-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  {isOpen && (
                    <input
                      type="checkbox"
                      aria-label={`Select run ${review.runId}`}
                      checked={selected.has(review.runId)}
                      onChange={() => toggle(review.runId)}
                    />
                  )}
                  <span
                    className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                      VERDICT_BADGE[review.verdict] ?? "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {review.verdict}
                  </span>
                  {isUnverifiableOnly(review) && (
                    <span
                      data-testid={`agent-review-unverifiable-${review.runId}`}
                      title="Flagged only because part of the tool evidence was too large to show the grader"
                      className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full"
                    >
                      Unverifiable
                    </span>
                  )}
                  <span className="text-sm font-medium text-slate-900">
                    {review.agentConfigId}
                  </span>
                  <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full">
                    {review.status}
                  </span>
                  <span className="ml-auto text-xs text-slate-500">
                    {formatCreatedAt(review.createdAt)}
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">
                  {review.rationale}
                </p>
                {review.suggestedFix && (
                  <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap">
                    <span className="font-medium">Suggested fix:</span> {review.suggestedFix}
                  </p>
                )}
                {review.resolutionReason && (
                  <p className="mt-2 text-sm text-slate-600">
                    <span className="font-medium">Resolved:</span> {review.resolutionReason}
                    {review.resolvedByEmail && ` — ${review.resolvedByEmail}`}
                    {review.resolvedAt && `, ${formatCreatedAt(review.resolvedAt)}`}
                  </p>
                )}
                <p className="mt-2 text-xs text-slate-400 font-mono" title={review.runId}>
                  Run {review.runId}
                </p>
              </li>
            );
          })}
        </ul>
      )}

      {hasNextPage && (
        <Button
          variant="secondary"
          size="sm"
          loading={!!isFetchingNextPage}
          loadingText="Loading..."
          onClick={() => fetchNextPage?.()}
        >
          Load more (showing {reviews.length} of {data?.total ?? reviews.length})
        </Button>
      )}
    </div>
  );
}
